import type { CreativeMediaProgressJob } from "./creativeMediaSchemas";

export interface CreativeMediaProgressSample {
  job: CreativeMediaProgressJob;
  receivedAt: number;
  serverNow: string;
  /** 选自已收到的真实样本，仅用于计时；不覆盖当前资源事实的新鲜度水位。 */
  clockAnchor?: { receivedAt: number; serverNow: string };
}

export function isCreativeMediaRunnable(status: CreativeMediaProgressJob["status"]) {
  return ["queued", "generating", "validating", "uploading"].includes(status);
}

export function mergeCreativeMediaProgressSamples(
  previous: readonly CreativeMediaProgressSample[], incoming: readonly CreativeMediaProgressSample[],
) {
  const jobs = new Map(previous.map((sample) => [sample.job.job_id, sample]));
  for (const sample of incoming) {
    const existing = jobs.get(sample.job.job_id);
    if (!existing || (!creativeMediaSampleConflict(existing, sample) && (sample.job.revision > existing.job.revision ||
        (sample.job.revision === existing.job.revision && Date.parse(sample.serverNow) >= Date.parse(existing.serverNow))))) {
      // 采用真实样本中较大的单调时钟偏移，重复/迟到 server_now 不重置已显示耗时。
      const previousAnchor = existing?.clockAnchor ?? existing;
      const incomingAnchor = sample.clockAnchor ?? sample;
      const keepTimeAnchor = previousAnchor && Date.parse(previousAnchor.serverNow) - previousAnchor.receivedAt >=
        Date.parse(incomingAnchor.serverNow) - incomingAnchor.receivedAt;
      jobs.set(sample.job.job_id, keepTimeAnchor ? {
        ...sample, clockAnchor: { receivedAt: previousAnchor.receivedAt, serverNow: previousAnchor.serverNow },
      } : sample);
    }
  }
  return [...jobs.values()];
}

export function creativeMediaSampleConflict(previous: CreativeMediaProgressSample, incoming: CreativeMediaProgressSample) {
  const a = previous.job, b = incoming.job;
  if (a.job_id !== b.job_id || b.revision < a.revision) return false;
  return a.organization_id !== b.organization_id || a.conversation_id !== b.conversation_id ||
    a.actor_user_id !== b.actor_user_id || a.origin_turn_id !== b.origin_turn_id ||
    a.created_at !== b.created_at || a.media_kind !== b.media_kind || a.operation !== b.operation ||
    // 云盘可用性独立于任务revision；严格合同已校验资源，仅比较版本化任务事实。
    (a.revision === b.revision && JSON.stringify({ ...a, resource_parts: [] }) !== JSON.stringify({ ...b, resource_parts: [] }));
}

export function creativeMediaElapsedMs(sample: CreativeMediaProgressSample, monotonicNow: number) {
  const { job } = sample;
  const anchor = sample.clockAnchor ?? sample;
  const runnable = isCreativeMediaRunnable(job.status);
  const end = job.completed_at ?? (runnable ? anchor.serverNow : sample.serverNow);
  return Math.max(0, Date.parse(end) - Date.parse(job.created_at) +
    (job.completed_at === null && runnable ? Math.max(0, monotonicNow - anchor.receivedAt) : 0));
}

export const creativeMediaStatusLabels: Record<CreativeMediaProgressJob["status"], string> = {
  draft: "已创建", queued: "等待生成", generating: "正在生成", validating: "处理生成结果",
  uploading: "处理并保存到云盘", succeeded: "生成完成", failed: "生成失败", canceled: "已取消",
};
